import { useState } from 'react'
import {
  type DppPassportFields,
  Countries,
  ProductIdentifierTypes,
  ProductIdentifierTypeLabels,
  DppProductTypes,
  DppProductTypeLabels,
  ProductCategoriesEU,
  ProductCategoryEULabels,
  EconomicOperatorRoles,
  EconomicOperatorRoleLabels,
  EconomicOperatorIdTypes,
  EconomicOperatorIdTypeLabels,
  DataCarrierTypes,
  DataCarrierTypeLabels,
  DataCarrierLocations,
  DataCarrierLocationLabels,
  DataAccessLevels,
  DataAccessLevelLabels,
  DataSourceTypes,
  DataSourceTypeLabels,
  DataUpdatePolicies,
  DataUpdatePolicyLabels,
  DataRetentionPeriods,
  DataRetentionPeriodLabels,
  PersonalDataOptions,
  PersonalDataOptionLabels,
  ProductUseContexts,
  ProductUseContextLabels,
  EnvironmentTypes,
  EnvironmentTypeLabels,
  UserGroupTypes,
  UserGroupTypeLabels,
  UseSeverityClasses,
  UseSeverityClassLabels,
  LoadRatings,
  LoadRatingLabels,
  ImpactVandalClasses,
  ImpactVandalClassLabels,
  FirePerformanceClasses,
  FirePerformanceClassLabels,
  SafetyStandards,
  SafetyStandardLabels,
  IntendedUseTypes,
  IntendedUseTypeLabels,
  AccessibilityDesigns,
  AccessibilityDesignLabels,
  WheelchairInterfaces,
  WheelchairInterfaceLabels,
  ArmrestOptions,
  ArmrestOptionLabels,
  HeightCategories,
  HeightCategoryLabels,
  TactileVisualCues,
  TactileVisualCueLabels,
  PrimaryMaterialGroups,
  PrimaryMaterialGroupLabels,
  CertifiedWoodSources,
  CertifiedWoodSourceLabels,
  RecycledContentBands,
  RecycledContentBandLabels,
  HazardousSubstanceFlags,
  HazardousSubstanceFlagLabels,
  CorrosionProtectionClasses,
  CorrosionProtectionClassLabels,
  UvWeatherResistances,
  UvWeatherResistanceLabels,
  SurfaceTreatments,
  SurfaceTreatmentLabels,
  EpdLcaReferences,
  EpdLcaReferenceLabels,
  CarbonFootprintScopes,
  CarbonFootprintScopeLabels,
  CarbonFootprintValueBands,
  CarbonFootprintValueBandLabels,
  MountingMethods,
  MountingMethodLabels,
  InstallationSettings,
  InstallationSettingLabels,
  InspectionRegimes,
  InspectionRegimeLabels,
  CleaningFrequencies,
  CleaningFrequencyLabels,
  ApprovedCleaningAgents,
  ApprovedCleaningAgentLabels,
  ServiceableComponentTypes,
  ServiceableComponentTypeLabels,
  SparePartsKitIds,
  SparePartsKitIdLabels,
  ExpectedServiceLifeBands,
  ExpectedServiceLifeBandLabels,
  RepairabilityRatings,
  RepairabilityRatingLabels,
  EndOfLifeRoutes,
  EndOfLifeRouteLabels,
  TakeBackSchemes,
  TakeBackSchemeLabels,
  ConformityMarkings,
  ConformityMarkingLabels,
  EuLegislationReferences,
  EuLegislationReferenceLabels,
  GppCriteriaOptions,
  GppCriteriaOptionLabels,
} from '@esf/shared'
import Autocomplete from '../Autocomplete'
import MultiSelectAutocomplete from '../MultiSelectAutocomplete'

interface DppPassportFormProps {
  data: Partial<DppPassportFields>
  onChange: (data: Partial<DppPassportFields>) => void
  disabled?: boolean
}

type SectionId =
  | 'identification'
  | 'operator'
  | 'production'
  | 'dataCarrier'
  | 'useContext'
  | 'safety'
  | 'accessibility'
  | 'materials'
  | 'environmental'
  | 'installation'
  | 'lifecycle'
  | 'compliance'

interface Section {
  id: SectionId
  label: string
  description: string
}

const sections: Section[] = [
  { id: 'identification', label: 'Identification', description: 'Product identification and classification' },
  { id: 'operator', label: 'Economic Operator', description: 'Responsible party information' },
  { id: 'production', label: 'Production', description: 'Manufacturing and batch details' },
  { id: 'dataCarrier', label: 'Data & Access', description: 'Data carrier and access settings' },
  { id: 'useContext', label: 'Use Context', description: 'Deployment environment and usage' },
  { id: 'safety', label: 'Safety', description: 'Safety standards and intended use' },
  { id: 'accessibility', label: 'Accessibility', description: 'Accessibility features' },
  { id: 'materials', label: 'Materials', description: 'Material composition and properties' },
  { id: 'environmental', label: 'Environmental', description: 'Carbon footprint and EPD' },
  { id: 'installation', label: 'Installation', description: 'Mounting and maintenance' },
  { id: 'lifecycle', label: 'Lifecycle', description: 'Service life and end-of-life' },
  { id: 'compliance', label: 'Compliance', description: 'Regulatory compliance' },
]

export default function DppPassportForm({ data, onChange, disabled }: DppPassportFormProps) {
  const [activeSection, setActiveSection] = useState<SectionId>('identification')

  const handleChange = <K extends keyof DppPassportFields>(
    field: K,
    value: DppPassportFields[K]
  ) => {
    onChange({ ...data, [field]: value })
  }

  const renderSelect = <T extends string>(
    field: keyof DppPassportFields,
    label: string,
    options: readonly T[],
    labels: Record<T, string>,
    description?: string
  ) => (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <select
        value={(data[field] as string) || ''}
        onChange={e => handleChange(field, (e.target.value || undefined) as DppPassportFields[typeof field])}
        disabled={disabled}
        className="filter-select w-full"
      >
        <option value="">-- Select --</option>
        {options.map(opt => (
          <option key={opt} value={opt}>{labels[opt]}</option>
        ))}
      </select>
      {description && <p className="text-xs text-gray-500 mt-1">{description}</p>}
    </div>
  )

  const renderInput = (
    field: keyof DppPassportFields,
    label: string,
    placeholder?: string,
    description?: string,
    readOnly?: boolean
  ) => (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <input
        type="text"
        value={(data[field] as string) || ''}
        onChange={e => handleChange(field, (e.target.value || undefined) as DppPassportFields[typeof field])}
        disabled={disabled || readOnly}
        readOnly={readOnly}
        className={`filter-input w-full ${readOnly ? 'bg-gray-100' : ''}`}
        placeholder={placeholder}
      />
      {description && <p className="text-xs text-gray-500 mt-1">{description}</p>}
    </div>
  )

  const renderCountrySelect = (
    field: 'manufacturerCountryCode' | 'productionCountryCode',
    label: string
  ) => {
    const countryOptions = Countries.map(c => ({
      value: c.cca2,
      label: `${c.flag} ${c.name}`,
    }))

    return (
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
        <Autocomplete
          options={countryOptions}
          value={data[field] || ''}
          onChange={val => handleChange(field, val || undefined)}
          placeholder="Select country..."
          disabled={disabled}
        />
      </div>
    )
  }

  const renderMultiSelect = <T extends string>(
    field: keyof DppPassportFields,
    label: string,
    options: readonly T[],
    labels: Record<T, string>,
    description?: string
  ) => {
    const multiOptions = options.map(opt => ({
      value: opt,
      label: labels[opt],
    }))
    const currentValues = (data[field] as T[] | undefined) || []

    return (
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
        <MultiSelectAutocomplete
          options={multiOptions}
          selectedValues={currentValues}
          onChange={vals => handleChange(field, vals.length > 0 ? vals : undefined as DppPassportFields[typeof field])}
          placeholder={`Select ${label.toLowerCase()}...`}
          disabled={disabled}
        />
        {description && <p className="text-xs text-gray-500 mt-1">{description}</p>}
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Section Navigation */}
      <div className="flex flex-wrap gap-2 pb-3 border-b border-gray-200">
        {sections.map(section => (
          <button
            key={section.id}
            type="button"
            onClick={() => setActiveSection(section.id)}
            className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
              activeSection === section.id
                ? 'bg-esf-navy text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {section.label}
          </button>
        ))}
      </div>

      {/* Section Description */}
      <div className="text-sm text-gray-600 italic">
        {sections.find(s => s.id === activeSection)?.description}
      </div>

      {/* Identification Section */}
      {activeSection === 'identification' && (
        <div className="space-y-4">
          {renderInput('dppId', 'DPP ID / URI', 'Auto-generated', 'Unique passport identifier', true)}

          <div className="grid grid-cols-2 gap-4">
            {renderSelect('productIdentifierType', 'Product identifier type', ProductIdentifierTypes, ProductIdentifierTypeLabels)}
            {renderInput('productIdentifierValue', 'Product identifier value', 'e.g., GTIN or product code')}
          </div>

          <div className="grid grid-cols-2 gap-4">
            {renderSelect('dppProductType', 'Product type', DppProductTypes, DppProductTypeLabels)}
            {renderInput('variantModel', 'Variant / model', 'Variant or configuration')}
          </div>

          <div className="grid grid-cols-2 gap-4">
            {renderInput('brandTradeName', 'Brand / trade name', 'Brand shown to users')}
            {renderSelect('productCategoryEU', 'Product category (EU)', ProductCategoriesEU, ProductCategoryEULabels, 'Regulatory product group')}
          </div>
        </div>
      )}

      {/* Economic Operator Section */}
      {activeSection === 'operator' && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            {renderSelect('responsibleOperatorRole', 'Responsible operator role', EconomicOperatorRoles, EconomicOperatorRoleLabels)}
            {renderSelect('responsibleOperatorIdType', 'Operator ID type', EconomicOperatorIdTypes, EconomicOperatorIdTypeLabels)}
          </div>

          <div className="grid grid-cols-2 gap-4">
            {renderInput('responsibleOperatorId', 'Operator ID', 'VAT number or other ID')}
            {renderInput('manufacturerName', 'Manufacturer name', 'Legal manufacturer')}
          </div>

          {renderCountrySelect('manufacturerCountryCode', 'Manufacturer country')}
        </div>
      )}

      {/* Production Section */}
      {activeSection === 'production' && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            {renderInput('productionSiteId', 'Production site ID', 'Factory/site identifier')}
            {renderCountrySelect('productionCountryCode', 'Production country')}
          </div>

          {renderInput('batchLotSerial', 'Batch / lot / serial', 'Production batch or serial code')}
        </div>
      )}

      {/* Data Carrier & Access Section */}
      {activeSection === 'dataCarrier' && (
        <div className="space-y-4">
          <h4 className="font-medium text-gray-900">Data Carrier</h4>
          <div className="grid grid-cols-2 gap-4">
            {renderSelect('dataCarrierType', 'Carrier type', DataCarrierTypes, DataCarrierTypeLabels)}
            {renderSelect('dataCarrierLocation', 'Carrier location', DataCarrierLocations, DataCarrierLocationLabels)}
          </div>

          <h4 className="font-medium text-gray-900 pt-4">Data Management</h4>
          <div className="grid grid-cols-2 gap-4">
            {renderSelect('dataAccessLevel', 'Access level', DataAccessLevels, DataAccessLevelLabels)}
            {renderSelect('dataSourceType', 'Data source', DataSourceTypes, DataSourceTypeLabels)}
          </div>

          <div className="grid grid-cols-2 gap-4">
            {renderSelect('dataUpdatePolicy', 'Update policy', DataUpdatePolicies, DataUpdatePolicyLabels)}
            {renderSelect('dataRetentionPeriod', 'Retention period', DataRetentionPeriods, DataRetentionPeriodLabels)}
          </div>

          {renderSelect('personalDataIncluded', 'Personal data included', PersonalDataOptions, PersonalDataOptionLabels)}
        </div>
      )}

      {/* Use Context Section */}
      {activeSection === 'useContext' && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            {renderSelect('productUseContext', 'Use context', ProductUseContexts, ProductUseContextLabels)}
            {renderSelect('environment', 'Environment', EnvironmentTypes, EnvironmentTypeLabels)}
          </div>

          <div className="grid grid-cols-2 gap-4">
            {renderSelect('userGroup', 'User group', UserGroupTypes, UserGroupTypeLabels)}
            {renderSelect('useSeverityClass', 'Use severity', UseSeverityClasses, UseSeverityClassLabels)}
          </div>

          <div className="grid grid-cols-2 gap-4">
            {renderSelect('loadRating', 'Load rating', LoadRatings, LoadRatingLabels)}
            {renderSelect('impactVandalClass', 'Impact / vandal class', ImpactVandalClasses, ImpactVandalClassLabels)}
          </div>

          {renderSelect('firePerformanceClass', 'Fire performance', FirePerformanceClasses, FirePerformanceClassLabels)}
        </div>
      )}

      {/* Safety Section */}
      {activeSection === 'safety' && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            {renderSelect('safetyStandardApplied', 'Safety standard applied', SafetyStandards, SafetyStandardLabels)}
            {renderSelect('intendedUse', 'Intended use', IntendedUseTypes, IntendedUseTypeLabels)}
          </div>
        </div>
      )}

      {/* Accessibility Section */}
      {activeSection === 'accessibility' && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            {renderSelect('accessibilityDesign', 'Accessibility design', AccessibilityDesigns, AccessibilityDesignLabels)}
            {renderSelect('wheelchairInterface', 'Wheelchair interface', WheelchairInterfaces, WheelchairInterfaceLabels)}
          </div>

          <div className="grid grid-cols-2 gap-4">
            {renderSelect('armrestsSupports', 'Armrests / supports', ArmrestOptions, ArmrestOptionLabels)}
            {renderSelect('heightCategory', 'Height category', HeightCategories, HeightCategoryLabels)}
          </div>

          {renderSelect('tactileVisualCues', 'Tactile / visual cues', TactileVisualCues, TactileVisualCueLabels)}
        </div>
      )}

      {/* Materials Section */}
      {activeSection === 'materials' && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            {renderSelect('primaryMaterialGroup', 'Primary material', PrimaryMaterialGroups, PrimaryMaterialGroupLabels)}
            {renderSelect('certifiedWoodSource', 'Certified wood source', CertifiedWoodSources, CertifiedWoodSourceLabels)}
          </div>

          <div className="grid grid-cols-2 gap-4">
            {renderSelect('recycledContentBand', 'Recycled content', RecycledContentBands, RecycledContentBandLabels)}
            {renderSelect('hazardousSubstancesPresent', 'Hazardous substances', HazardousSubstanceFlags, HazardousSubstanceFlagLabels)}
          </div>

          <div className="grid grid-cols-2 gap-4">
            {renderSelect('corrosionProtectionClass', 'Corrosion protection', CorrosionProtectionClasses, CorrosionProtectionClassLabels)}
            {renderSelect('uvWeatherResistance', 'UV / weather resistance', UvWeatherResistances, UvWeatherResistanceLabels)}
          </div>

          {renderSelect('surfaceTreatment', 'Surface treatment', SurfaceTreatments, SurfaceTreatmentLabels)}
        </div>
      )}

      {/* Environmental Section */}
      {activeSection === 'environmental' && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            {renderSelect('epdLcaReference', 'EPD / LCA reference', EpdLcaReferences, EpdLcaReferenceLabels)}
            {renderInput('epdLcaReferenceId', 'EPD / LCA reference ID', 'Link or document ID')}
          </div>

          <div className="grid grid-cols-2 gap-4">
            {renderSelect('carbonFootprintScope', 'Carbon footprint scope', CarbonFootprintScopes, CarbonFootprintScopeLabels)}
            {renderSelect('carbonFootprintValueBand', 'Carbon footprint band', CarbonFootprintValueBands, CarbonFootprintValueBandLabels)}
          </div>
        </div>
      )}

      {/* Installation & Maintenance Section */}
      {activeSection === 'installation' && (
        <div className="space-y-4">
          <h4 className="font-medium text-gray-900">Installation</h4>
          <div className="grid grid-cols-2 gap-4">
            {renderSelect('mountingMethod', 'Mounting method', MountingMethods, MountingMethodLabels)}
            {renderSelect('typicalInstallationSetting', 'Installation setting', InstallationSettings, InstallationSettingLabels)}
          </div>

          <h4 className="font-medium text-gray-900 pt-4">Maintenance</h4>
          <div className="grid grid-cols-2 gap-4">
            {renderSelect('inspectionRegime', 'Inspection regime', InspectionRegimes, InspectionRegimeLabels)}
            {renderSelect('cleaningFrequency', 'Cleaning frequency', CleaningFrequencies, CleaningFrequencyLabels)}
          </div>

          <div className="grid grid-cols-2 gap-4">
            {renderSelect('approvedCleaningAgents', 'Cleaning agents', ApprovedCleaningAgents, ApprovedCleaningAgentLabels)}
            {renderSelect('sparePartsKitId', 'Spare parts kit', SparePartsKitIds, SparePartsKitIdLabels)}
          </div>

          {renderMultiSelect('serviceableComponents', 'Serviceable components', ServiceableComponentTypes, ServiceableComponentTypeLabels)}
        </div>
      )}

      {/* Lifecycle Section */}
      {activeSection === 'lifecycle' && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            {renderSelect('expectedServiceLife', 'Expected service life', ExpectedServiceLifeBands, ExpectedServiceLifeBandLabels)}
            {renderSelect('repairabilityRatingDpp', 'Repairability rating', RepairabilityRatings, RepairabilityRatingLabels)}
          </div>

          <div className="grid grid-cols-2 gap-4">
            {renderSelect('endOfLifeRoute', 'End of life route', EndOfLifeRoutes, EndOfLifeRouteLabels)}
            {renderSelect('takeBackScheme', 'Take back scheme', TakeBackSchemes, TakeBackSchemeLabels)}
          </div>
        </div>
      )}

      {/* Compliance Section */}
      {activeSection === 'compliance' && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            {renderSelect('conformityMarking', 'Conformity marking', ConformityMarkings, ConformityMarkingLabels)}
            {renderSelect('gppCriteriaMet', 'GPP criteria met', GppCriteriaOptions, GppCriteriaOptionLabels)}
          </div>

          {renderMultiSelect('euLegislationReferences', 'EU legislation references', EuLegislationReferences, EuLegislationReferenceLabels, 'Select all applicable legislation')}
        </div>
      )}
    </div>
  )
}
